import React, { memo } from "react";
import Modal from "react-modal";
import { IoClose } from "react-icons/io5";

import styles from "./homePage.module.scss";
import AddedBy from "../../components/addedBy";
import { convertToCloudFrontUrl } from "../../helper";
import { formatMongoDBTime } from "../../utills/dateAndTime";

/**
 * Read-only detail view for a post, reel or guide.
 *
 * Deliberately not the edit form. Clicking a card is a browsing action, and
 * dropping an admin into editable inputs means a stray keystroke on a card they
 * only wanted to look at can be saved. It also mirrors the Guides tab, which
 * has always opened a viewer rather than an editor.
 */

const noImageUrl =
  "https://static.vecteezy.com/system/resources/previews/004/141/669/non_2x/no-photo-or-blank-image-icon-loading-images-or-missing-image-mark-image-not-available-or-image-coming-soon-sign-simple-nature-silhouette-in-frame-isolated-illustration-vector.jpg";

/** One label/value row. Renders nothing when there is no value to show. */
const Field = ({ label, children, show = true }) =>
  show ? (
    <div className={styles.viewPostField}>
      <span className={styles.viewPostLabel}>{label}</span>
      <div className={styles.viewPostValue}>{children}</div>
    </div>
  ) : null;

const Chips = ({ items }) =>
  items.length > 0 ? (
    <div className={styles.viewPostChips}>
      {items.map((item, i) => (
        <span key={i} className={styles.viewPostChip}>
          {item}
        </span>
      ))}
    </div>
  ) : (
    <span className={styles.viewPostEmpty}>—</span>
  );

const ViewPostPopup = ({ isOpen, onClose, post }) => {
  if (!post) return null;

  const tags = (post.tags || []).map((t) => t.tagName).filter(Boolean);
  const genres = (post.category || []).map((c) => c.label).filter(Boolean);

  // Guides carry a carousel; posts and reels carry a single asset.
  const carousel = (post.carouselImages || [])
    .map((item) =>
      item.image?.uploadedImageUrl
        ? convertToCloudFrontUrl(item.image.uploadedImageUrl)
        : null
    )
    .filter(Boolean);

  const isVideo = post.mediaType === "video";
  const imageUrl = post.posts?.uploadedImageUrl
    ? convertToCloudFrontUrl(post.posts.uploadedImageUrl)
    : null;
  const videoUrl = post.reels?.uploadedImageUrl
    ? convertToCloudFrontUrl(post.reels.uploadedImageUrl)
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal_body_lg"
      overlayClassName="modal_overlay"
      appElement={document.getElementById("root")}
    >
      <div className={`box_wrapper ${styles.viewPostWrapper}`}>
        <div className={styles.viewPostHeader}>
          <h4 className="mb-0">{post.title || "Untitled"}</h4>
          <button
            type="button"
            className={styles.viewPostCloseButton}
            onClick={onClose}
            aria-label="Close post details"
          >
            <IoClose size={22} />
          </button>
        </div>

        <div className={`modal_content_md ${styles.viewPostContent}`}>
          <div className={styles.viewPostMedia}>
            {isVideo && videoUrl ? (
              // `controls` and nothing else — no autoplay, because opening a
              // card should not start sound in a room full of people.
              <video controls preload="metadata" className={styles.viewPostAsset}>
                <source src={videoUrl} type="video/mp4" />
              </video>
            ) : carousel.length > 0 ? (
              <div className={styles.viewPostCarousel}>
                {carousel.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt={`Page ${i + 1}`}
                    className={styles.viewPostAsset}
                  />
                ))}
              </div>
            ) : (
              <img
                src={imageUrl || noImageUrl}
                alt=""
                className={styles.viewPostAsset}
              />
            )}
          </div>

          <Field label="Type">{post.mediaType || "—"}</Field>
          <Field label="Category Type">{post.type || "—"}</Field>

          <Field label="Pillar" show={Boolean(post.pillar?.pillarName)}>
            {post.pillar?.pillarName}
            {post.subPillar?.subPillarName
              ? ` › ${post.subPillar.subPillarName}`
              : ""}
          </Field>

          <Field label="Tags">
            <Chips items={tags} />
          </Field>

          <Field label="Genre">
            <Chips items={genres} />
          </Field>

          <Field label="Description" show={Boolean(post.description)}>
            {/* Descriptions are plain text in the form, so they are rendered as
                text — not dangerouslySetInnerHTML, which would execute anything
                that got in there. */}
            <p className="mb-0">{post.description}</p>
          </Field>

          <Field label="Visibility">
            {post.isPostVisible ? "Visible" : "Hidden"}
          </Field>

          <Field label="Added by">
            <AddedBy record={post} />
          </Field>

          <Field label="Created" show={Boolean(post.createdAt)}>
            {formatMongoDBTime(post.createdAt)}
          </Field>
        </div>
      </div>
    </Modal>
  );
};

export default memo(ViewPostPopup);
